import { H4, H6 } from '@blueprintjs/core';
import AddNewButton from 'Components/Generic/Buttons/AddNewButton';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { formatMoney } from 'Util';
import styles from './SplitRequestPanel.module.css';

const getBlankTransactionObject = (id) => {
  return { id, lines: new Set(), transaction_type: 'PCARD' };
};
const SplitRequestPanel = ({
  request = {},
  onConfirm = () => {},
  onCancel = () => {},
}) => {
  const [lineItemList, setLineItemList] = useState(
    request.line_items.filter((line) => !line.assigned_transaction_id),
  );
  const [transactionList, setTransactionList] = useState([
    getBlankTransactionObject('1'),
    getBlankTransactionObject('2'),
  ]);
  const [isDragging, setIsDragging] = useState(false);

  const removeLineFromTrans = (lineObject, transObject) => {
    transObject.lines.delete(lineObject);
    setTransactionList([...transactionList]);
  };

  const onDragStart = () => {
    setIsDragging(true);
  };

  const onDragEnd = (result) => {
    setIsDragging(false);
    const { source, destination, draggableId } = result;

    if (!destination || source.droppableId === destination.droppableId) {
      return;
    }
    let sourceLine = lineItemList.find((line) => `${line.id}` === draggableId);
    if (source.droppableId !== 'line-items') {
      let sourceTrans = transactionList.find(
        (trans) =>
          trans.lines.has(sourceLine) && trans.id !== destination.droppableId,
      );
      sourceTrans.lines.delete(sourceLine);
    }
    let destinationTrans = transactionList.find(
      (trans) => trans.id === destination.droppableId,
    );
    destinationTrans && destinationTrans.lines.add(sourceLine);

    setTransactionList([...transactionList]);
  };

  const getLineLabel = (line) => {
    return `${line.description} (Qty: ${line.quantity} X $${formatMoney(
      line.unit_price,
    )} = $${formatMoney(line.quantity * line.unit_price)})`;
  };

  let claimedLines = transactionList.map((trans) => [...trans.lines]).flat();

  const getDraggableLineComponent = (line, index, trans) => {
    return (
      <Draggable key={line.id} draggableId={`${line.id}`} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={
              trans && !snapshot.isDragging
                ? styles.line_item_container_full
                : styles.line_item_container
            }>
            <div
              className={`${
                trans ? styles.line_item_flat : styles.line_item_card
              } ${snapshot.isDragging ? styles.is_dragging : ''}`}>
              {getLineLabel(line)}
            </div>
            {trans && claimedLines.includes(line) && !snapshot.isDragging && (
              <DeleteButton
                miniVersion
                useXIcon
                onDeleteItem={() => removeLineFromTrans(line, trans)}
                tooltipText={'Remove Line'}
              />
            )}
          </div>
        )}
      </Draggable>
    );
  };

  let requestLineItems = lineItemList
    .filter((line) => !claimedLines.includes(line))
    .map((line, index) => getDraggableLineComponent(line, index));

  const removeTransaction = (transObject) => {
    setTransactionList(
      transactionList.filter((trans) => trans !== transObject),
    );
  };

  let transactions = transactionList.map((trans, index) => (
    <div key={trans.id} className={styles.transaction_outer_box}>
      <div className={styles.transaction_card}>
        <div className={styles.transaction_outer_box}>
          <H4
            style={{
              marginBottom: 0,
            }}>{`${trans.id}:`}</H4>
          <div className={styles.transaction_line_list}>
            <Droppable key={trans.id} droppableId={trans.id} index={index}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`${styles.transaction_line_list_drop_box} ${
                    snapshot.isDraggingOver ? styles.drop_shadow : ''
                  }`}>
                  {[...trans.lines].map((line, ind) =>
                    getDraggableLineComponent(line, ind, trans),
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </div>
      </div>

      {index > 1 && (
        <DeleteButton
          miniVersion
          useXIcon
          onDeleteItem={() => removeTransaction(trans)}
          tooltipText={'Remove Transaction'}
        />
      )}
    </div>
  ));

  const addTransaction = () => {
    setTransactionList([
      ...transactionList,
      getBlankTransactionObject(`${transactionList.length + 1}`),
    ]);
  };

  let addTransactionButton = transactionList.length < lineItemList.length && (
    <AddNewButton
      clickHandler={addTransaction}
      buttonText={'Add Transaction'}
    />
  );

  const saveTransaction = () => {
    onConfirm(transactionList);
  };

  let readyToSave =
    claimedLines.length === lineItemList.length &&
    transactionList.every((trans) => trans.lines?.size > 0);
  let saveButton = (
    <SaveButton disabled={!readyToSave} clickHandler={saveTransaction} />
  );

  let cancelButton = <CancelButton fill={false} onCancel={onCancel} />;

  let buttonFooter = (
    <ButtonFooter>
      {saveButton}
      {cancelButton}
    </ButtonFooter>
  );

  return (
    <div className={styles.containing_div}>
      {isDragging && <div className={styles.drag_overlay}></div>}
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <H6>Line Items</H6>
        <Droppable droppableId='line-items'>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              className={`${styles.line_item_list_container} ${
                snapshot.isDraggingOver ? styles.drop_shadow : ''
              }`}
              {...provided.droppableProps}>
              {requestLineItems}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <H6 style={{ paddingTop: 12 }}>Transactions</H6>
        {transactions}
        {addTransactionButton}
        {buttonFooter}
      </DragDropContext>
    </div>
  );
};

SplitRequestPanel.propTypes = {
  request: PropTypes.shape({ line_items: PropTypes.array }),
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

export default SplitRequestPanel;
